import { useState, useEffect, useCallback } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle, 
} from "../../ui/card";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { InputFieldset } from 'ui/inputFieldset';
import { Label } from "../../ui/label";
import { Badge } from "../../ui/badge";
import { Checkbox } from "../../ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../ui/tabs";
import {
  Role,
  RolePermissions,
  PermissionAction,
} from "../../types/permissions";
import {
  Shield,
  Plus,
  Edit,
  Trash2,
  Search,
  X,
  CheckSquare,
  Lock,
} from "lucide-react";

import {
  getRoles,
  deleteRole,
  ApiRole,
  getRoleWithPermissions,
} from "../../services/roleService";
import {
  getModulesWithScreens,
  createRoleWithPermissions,
  CreateRoleWithPermissionsPayload,
  updateRoleWithPermissions,
} from "../../services/moduleService";
import { Switch } from "../../ui/switch";
import { toast } from "sonner";
import { useScreenPermission } from "../../hooks/useScreenPermission";
import { FullScreenLoading } from "components/ui/loader";
import { usePageHeader } from "contexts/PageHeaderContext";
import Pagination from "ui/pagination";

interface DynamicScreen {
  id: string;
  moduleId: number;
  moduleName: string;
  screenName: string;
  shortDescription: string;
}

// interface RolePermissionPayload {
//   roleModulePermissionId: number;
//   roleId: number;
//   moduleScreenId: number;
//   canView: boolean;
//   canAdd: boolean;
//   canEdit: boolean;
//   canDelete: boolean;
//   canApprove: boolean;
//   canLock: boolean;
//   canRestore: boolean;
//   canManageUsers: boolean;
//   isActive: boolean;
//   updatedBy: number;
//   performedBy: number;
// }

export const RoleManagementPage = () => {
  const { setPageHeader } = usePageHeader();
  const [roles, setRoles] = useState<Role[]>([]);

  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState<"list" | "form">("list");
  const [editingRole, setEditingRole] = useState<Role | null>(null);

  const [formName, setFormName] = useState("");
  const [formDescription, setFormDescription] = useState("");
  const [formPermissions, setFormPermissions] = useState<RolePermissions>({});

  const [formAvailableForDelegation, setFormAvailableForDelegation] =
    useState(false);

  const storedUser = localStorage.getItem("fhims_auth_user");

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const loggedInUser = storedUser ? JSON.parse(storedUser) : null;

  const [dynamicModules, setDynamicModules] = useState<DynamicScreen[]>([]);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const { canAdd, canEdit, canDelete, screenName, icon: ScreenIcon } = useScreenPermission();

  const [roleToDelete, setRoleToDelete] = useState<Role | null>(null);

  const currentUserEmail =
    localStorage.getItem("currentUserEmail") || "admin@fhims.com";
  const isVisaManager = currentUserEmail === "visamanager@fhims.com";

  const lettersOnlyRegex = /^[A-Za-z\s]*$/;

  const permissionActions: PermissionAction[] = [
    "view",
    "add",
    "edit",
    "delete",
    "approve",
  ];
  const manageUsersAction = "manageUsers" as const;

  const fetchRoles = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getRoles(currentPage, pageSize);

      setTotalCount(data.totalCount);

      const apiRoles: ApiRole[] = data.roles;

      // Load roles with permissions
      const rolesWithPermissions = await Promise.all(
        apiRoles.map((r: ApiRole) => getRoleWithPermissions(r.roleId)),
      );

      const mapped: Role[] = rolesWithPermissions.map((r) => {
        const permissionMap: RolePermissions = {};
        const enabledModuleNames = new Set<string>();

        r.permissions.forEach((p) => {
          permissionMap[p.moduleScreenId] = {
            view: p.canView,
            add: p.canAdd,
            edit: p.canEdit,
            delete: p.canDelete,
            approve: p.canApprove,
            lock: p.canLock,
            restore: p.canRestore,
            manageUsers: p.canManageUsers ?? false,
          };

          if (
            p.canView ||
            p.canAdd ||
            p.canEdit ||
            p.canDelete ||
            p.canApprove ||
            p.canLock ||
            p.canRestore ||
            p.canManageUsers
          ) {
            enabledModuleNames.add(p.moduleName);
          }
        });

        return {
          id: String(r.roleId),
          name: r.roleName,
          description: r.roleDescription || "",
          permissions: permissionMap,
          enabledModules: Array.from(enabledModuleNames),
          isSystemRole: r.isSystemRole,
          roleCode: r.roleCode ?? "",
          defaultDashboardUrl: "",
          isActive: r.isActive,
          isAvailableForDelegation: r.isAvailableForDelegation,
          createdDate: "",
        };
      });

      setRoles(mapped);
    } catch (err) {
      console.error("Failed loading roles:", err);
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize]);

  useEffect(() => {
    if (viewMode === "list") {
      setPageHeader({
        title: screenName,
        icon: <ScreenIcon className="h-6 w-6" />,
      });
    } else {
      setPageHeader({
        title: editingRole ? "Edit Role" : "Create New Role",
        icon: <Shield className="h-4 w-4" />,
        onBack: handleBackToList,
      });
    }

    return () => setPageHeader(null);
  }, [
    viewMode,
    editingRole,
    screenName,
    ScreenIcon,
    setPageHeader,
  ]);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles, currentPage, pageSize]);

  useEffect(() => {
    (async () => {
      const data = await getModulesWithScreens();

      const flattened: DynamicScreen[] = data.flatMap((module) =>
        module.screens.map((screen) => ({
          id: screen.moduleScreenId.toString(),
          moduleId: module.moduleId,
          moduleName: module.moduleName,
          screenName: screen.screenName,
          shortDescription: screen.shortDescription,
        })),
      );

      setDynamicModules(flattened);
    })();
  }, []);

  const groupedByModule = dynamicModules.reduce<
    Record<string, DynamicScreen[]>
  >((acc, screen) => {
    if (!acc[screen.moduleName]) acc[screen.moduleName] = [];
    acc[screen.moduleName].push(screen);
    return acc;
  }, {});

  const handleOpenForm = async (role?: Role) => {
    try {
    setLoading(true);
    if (role) {
      setEditingRole(role);

      const fullRole = await getRoleWithPermissions(parseInt(role.id, 10));

      setFormName(fullRole.roleName);
      setFormDescription(fullRole.roleDescription || "");
      setFormAvailableForDelegation(fullRole.isAvailableForDelegation);

      const formatted: RolePermissions = {};

      fullRole.permissions.forEach((p) => {
        formatted[p.moduleScreenId] = {
          view: p.canView,
          add: p.canAdd,
          edit: p.canEdit,
          delete: p.canDelete,
          approve: p.canApprove,
          lock: p.canLock,
          restore: p.canRestore,
          manageUsers: p.canManageUsers ?? false,
          roleModulePermissionId: p.roleModulePermissionId,
        };
      });

      setFormPermissions(formatted);
    } else {
      setEditingRole(null);
      setFormName("");
      setFormDescription("");

      const emptyPermissions: RolePermissions = {};
      dynamicModules.forEach((screen) => {
        emptyPermissions[screen.id] = {
          view: false,
          add: false,
          edit: false,
          delete: false,
          approve: false,
          lock: false,
          restore: false,
          manageUsers: false,
        };
      });

      setFormPermissions(emptyPermissions);
    }

    setTimeout(() => setViewMode("form"), 0);
    } finally {
      setLoading(false);
    }
  };

  const handleBackToList = () => {
    setViewMode("list");
    setEditingRole(null);
  };

  const handleTogglePermission = (
    moduleScreenId: string,
    action: PermissionAction,
  ) => {
    setFormPermissions((prev) => ({
      ...prev,
      [moduleScreenId]: {
        ...prev[moduleScreenId],
        [action]: !prev[moduleScreenId]?.[action],
      },
    }));
  };

  const handleSelectAll = (moduleScreenId: string) => {
    const allSelected = permissionActions.every(
      (action) => formPermissions[moduleScreenId]?.[action],
    );
    setFormPermissions((prev) => ({
      ...prev,
      [moduleScreenId]: {
        view: !allSelected,
        add: !allSelected,
        edit: !allSelected,
        delete: !allSelected,
        approve: !allSelected,
        lock: !allSelected,
        restore: !allSelected,
        manageUsers: !allSelected,
      },
    }));
  };

  const handleSaveRole = async () => {
    if (!formName.trim()) {
      toast.error("Please enter a role name");
      return;
    }

    if (!lettersOnlyRegex.test(formName.trim())) {
      toast.error("Role Name can contain only letters and spaces.");
      return;
    }

    if (formDescription.trim() && !lettersOnlyRegex.test(formDescription.trim())) {
      toast.error("Description can contain only letters and spaces.");
      return;
    }

    const storedUser = localStorage.getItem("fhims_auth_user");
    const loggedInUser = storedUser ? JSON.parse(storedUser) : null;

    if (!loggedInUser?.id) {
      toast.error("User session expired. Please login again.");
      return;
    }

    const userId = Number(loggedInUser.id);

    setIsSaving(true);

    try {
      const permissionItems = dynamicModules
        .map((screen) => {
          const perms = formPermissions[screen.id];
          if (!perms) return null;

          const hasAnyPermission =
            perms.view ||
            perms.add ||
            perms.edit ||
            perms.delete ||
            perms.approve ||
            perms.lock ||
            perms.restore ||
            perms.manageUsers;

          if (!hasAnyPermission) return null;

          // find existing permission if editing
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const existingPermission = editingRole
            ? editingRole.permissions[Number(screen.id)]
            : null;

          return {
            roleModulePermissionId: perms.roleModulePermissionId,
            roleId: editingRole ? Number(editingRole.id) : undefined,
            moduleId: screen.moduleId,
            moduleScreenId: Number(screen.id),
            canView: perms.view,
            canAdd: perms.add,
            canEdit: perms.edit,
            canDelete: perms.delete,
            canApprove: perms.approve,
            canLock: perms.lock,
            canRestore: perms.restore,
            canManageUsers: perms.manageUsers ?? false,
            isActive: true,
            createdBy: userId,
          };
        })
        .filter((item): item is NonNullable<typeof item> => item !== null);

      if (permissionItems.length === 0) {
        toast.error("Please assign at least one permission");
        setIsSaving(false);
        return;
      }

      let payload: CreateRoleWithPermissionsPayload;

      if (editingRole) {
        payload = {
          role: {
            roleId: Number(editingRole.id),
            roleCode: editingRole.roleCode,
            roleName: formName.trim(),
            description: formDescription.trim(),
            isSystemRole: editingRole.isSystemRole ?? false,
            isAvailableForDelegation: formAvailableForDelegation,
            defaultDashboardUrl: "",
            isActive: true,
            updatedBy: userId,
          },
          permissions: permissionItems,
        };

        await updateRoleWithPermissions(payload);
        toast.success("Role updated successfully");
      } else {
        const baseCode = formName
          .toLowerCase()
          .trim()
          .replace(/\s+/g, "-")
          .replace(/[^a-z0-9-]/g, "");

        const uniqueRoleCode = `${baseCode}-${Date.now()}`;

        payload = {
          role: {
            roleCode: uniqueRoleCode,
            roleName: formName.trim(),
            description: formDescription.trim(),
            isSystemRole: false,
            isAvailableForDelegation: formAvailableForDelegation,
            defaultDashboardUrl: "",
            createdBy: userId,
          },
          permissions: permissionItems,
        };

        await createRoleWithPermissions(payload);
        toast.success("Role created successfully");
      }

      await fetchRoles();
      handleBackToList();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      console.error("Error saving role:", error);

      const backendMessage =
        error?.response?.data?.message ||
        error?.response?.data?.Message ||
        "Failed to save role";

      toast.error(backendMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteRole = async (role: Role) => {
    try {
      setLoading(true);
      const result = await deleteRole(Number(role.id));

      const response = result as unknown as {
        success?: boolean;
        message?: string;
      };

      if (response.success) {
        setRoles((prev) => prev.filter((r) => r.id !== role.id));

        toast.success(response.message || "Role deleted successfully.");
      } else {
        toast.error(response.message || "Role could not be deleted.");
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      console.error("Delete failed:", error);

      const backendMessage =
        error?.response?.data?.message ||
        error?.response?.data?.Message ||
        "Failed to delete role";

      toast.error(backendMessage);
    } finally {
      setLoading(false);
    }
  };

  // Filter modules for visa manager - show only Operations category and only Visa Processing module

  if (viewMode === "form") {
    return (
      <>
        {loading && <FullScreenLoading />}
        {isSaving && <FullScreenLoading />}
        <div className="space-y-4 sm:space-y-6 min-h-screen">
          {/* Header with Back Button - Mobile Responsive */}
          <div className="flex flex-col gap-3 sm:gap-4">
            {/* Title Row with Back Button */}
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm sm:text-base text-gray-600">
              Configure role details and assign granular permissions
            </p>
              <div className="hidden sm:flex gap-3 items-center flex-shrink-0">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={handleBackToList}
                  className="hover:bg-gray-100 h-10 text-sm"
                >
                  <X className="w-4 h-4 mr-2" />
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={handleSaveRole}
                  className="text-white shadow-lg hover:opacity-90 transition-opacity h-10 text-sm"
                  style={{ backgroundColor: "#5B9BD5" }}
                  disabled={isSaving}
                >
                  {isSaving
                    ? editingRole
                      ? "Updating..."
                      : "Creating..."
                    : editingRole
                      ? "Update Role"
                      : "Create Role"}
                </Button>
              </div>
            </div>

            {/* Description Row */}


            {/* Mobile Action Buttons */}
            <div className="flex sm:hidden gap-2 w-full">
              <Button
                type="button"
                variant="ghost"
                onClick={handleBackToList}
                className="hover:bg-gray-100 flex-1 h-9 text-sm"
              >
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleSaveRole}
                className="text-white shadow-lg hover:opacity-90 transition-opacity flex-1 h-9 text-sm"
                style={{ backgroundColor: "#5B9BD5" }}
                disabled={isSaving}
              >
                {isSaving
                  ? editingRole
                    ? "Updating..."
                    : "Creating..."
                  : editingRole
                    ? "Update Role"
                    : "Create Role"}
              </Button>
            </div>
          </div>

          {/* Role Information Card */}
          <Card className="bg-white border-gray-200 shadow-sm" style={{ gap: "4px", marginTop: "10px" }}>
            <CardHeader>
              <CardTitle
                className="text-base sm:text-lg"
                style={{ color: "#5B9BD5" }}
              >
                Role Information
              </CardTitle>
              <CardDescription className="text-xs sm:text-sm text-gray-500 !mt-0">
                Basic details about this role
              </CardDescription>
            </CardHeader>

            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-2">
                <div className="space-y-2">
                  <InputFieldset
                    label="Role Name"
                    required
                    value={formName}
                    maxLength={30}
                    onChange={(e) => {
                      const value = e.target.value;

                      if (lettersOnlyRegex.test(value)) {
                        setFormName(value);
                      }
                    }}
                    placeholder="e.g., Department Manager"
                    className="border-gray-300 focus:border-[#5B9BD5] focus:ring-[#5B9BD5] text-sm"
                  />
                </div>

                <div className="space-y-2">
                  <InputFieldset
                    label="Description"
                    value={formDescription}
                    onChange={(e) => {
                      const value = e.target.value;

                      if (lettersOnlyRegex.test(value)) {
                        setFormDescription(value);
                      }
                    }}
                    placeholder="Brief role description"
                    className="border-gray-300 focus:border-[#5B9BD5] focus:ring-[#5B9BD5] text-sm"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Role Delegation Settings Card - Hidden for Visa Manager */}
          {!isVisaManager && (
            <Card className="bg-white shadow-lg border-2 border-gray-200" style={{ marginTop: "10px" }}>
              <CardContent className="p-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-5 bg-gradient-to-r from-[#5B9BD5]/10 to-white rounded-lg border-2 border-[#5B9BD5]/30">
                    <div className="flex-1">
                      <Label className="text-gray-900 cursor-pointer flex items-center gap-2 mb-1">
                        <Shield
                          className="w-5 h-5"
                          style={{ color: "#5B9BD5" }}
                        />
                        Role Available for Delegation
                      </Label>
                      <p className="text-xs text-gray-600">
                        When enabled, all users can see and assign this role when
                        creating new users
                      </p>
                    </div>
                    <Switch
                      checked={formAvailableForDelegation}
                      onCheckedChange={setFormAvailableForDelegation}
                      className="data-[state=checked]:bg-[#5B9BD5] ml-4"
                    />
                  </div>

                  {formAvailableForDelegation && (
                    <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-start gap-2">
                        <CheckSquare className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-sm text-green-900">
                            This role will be visible for assignment
                          </p>
                          <p className="text-xs text-green-700 mt-1">
                            Users creating new accounts will see this role in the
                            role selection list
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {!formAvailableForDelegation && (
                    <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                      <div className="flex items-start gap-2">
                        <Lock className="w-5 h-5 text-gray-600 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-sm text-gray-900">
                            This role will be restricted
                          </p>
                          <p className="text-xs text-gray-600 mt-1">
                            Only administrators can assign this role to users
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Permission Matrix Card */}
          <Card
            className="bg-white border-gray-200 shadow-sm"
            style={{ gap: "0px", marginTop: "10px" }}
          >
            <CardHeader
              className="sm:p-6"
              style={{ padding: "24px 24px 0px 24px" }}
            >
              <CardTitle
                className="text-base sm:text-lg"
                style={{ color: "#5B9BD5" }}
              >
                Permission Matrix
              </CardTitle>
              <CardDescription className="text-xs sm:text-sm text-gray-500">
                Assign granular permissions for each module across all categories
              </CardDescription>
            </CardHeader>
            <CardContent className="p-4 sm:p-6 pt-0">
              <Tabs
                defaultValue={Object.keys(groupedByModule)[0]}
                className="w-full"
              >
                <div className="mt-4 sm:mt-0 mb-4 sm:mb-6">
                  <TabsList className="w-full bg-gray-100 p-1 gap-1 h-auto grid grid-cols-2 sm:flex sm:flex-nowrap">
                    {Object.keys(groupedByModule).map((moduleName) => (
                      <TabsTrigger
                        key={moduleName}
                        value={moduleName}
                        className="data-[state=active]:bg-white data-[state=active]:shadow-sm text-xs sm:text-sm px-3 py-2text-center rounded-md"
                        style={{
                          color:
                            moduleName === "Operations" ? "#5B9BD5" : undefined,
                        }}
                      >
                        {moduleName}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                </div>
                {Object.entries(groupedByModule).map(([moduleName, modules]) => (
                  <TabsContent
                    key={moduleName}
                    value={moduleName}
                    className="mt-0"
                  >
                    {/* Desktop Table View */}
                    <div className="hidden md:block border border-gray-200 rounded-lg overflow-hidden">
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead className="bg-gray-50">
                            <tr>
                              <th
                                className="text-left p-2 border-b border-gray-200 sticky left-0 bg-gray-50 z-10 min-w-[200px]"
                                style={{ color: "#5B9BD5" }}
                              >
                                Screen Name
                              </th>
                              <th
                                className="text-center p-2 border-b border-l border-gray-200 min-w-[80px]"
                                style={{ color: "#5B9BD5" }}
                              >
                                Select All
                              </th>
                              {permissionActions.map((action) => (
                                <th
                                  key={action}
                                  className="text-center p-4 border-b border-l border-gray-200 capitalize min-w-[90px]"
                                  style={{ color: "#5B9BD5" }}
                                >
                                  {action}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {modules.map((screen) => {
                              const modulePermissions =
                                formPermissions[screen.id] || {};
                              const allSelected = permissionActions.every(
                                (action) => modulePermissions[action],
                              );

                              return (
                                <tr
                                  key={screen.id}
                                  className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                                >
                                  <td className="p-2 sticky left-0 bg-white hover:bg-gray-50 z-10">
                                    <div>
                                      <p
                                        className="text-xs"
                                        style={{ color: "#5B9BD5" }}
                                      >
                                        {screen.screenName}
                                      </p>
                                      <p className="text-[10px] text-gray-500">
                                        {" "}
                                        {screen.shortDescription}
                                      </p>
                                    </div>
                                  </td>
                                  <td className="text-center p-2 border-l border-gray-100">
                                    <div className="flex justify-center">
                                      <Checkbox
                                        checked={allSelected}
                                        onCheckedChange={() =>
                                          handleSelectAll(String(screen.id))
                                        }
                                        className="border-gray-300 data-[state=checked]:bg-[#5B9BD5] data-[state=checked]:border-[#5B9BD5]"
                                      />
                                    </div>
                                  </td>
                                  {permissionActions.map((action) => (
                                    <td
                                      key={action}
                                      className="text-center p-4 border-l border-gray-100"
                                    >
                                      <div className="flex justify-center">
                                        <Checkbox
                                          checked={
                                            modulePermissions[action] || false
                                          }
                                          onCheckedChange={() =>
                                            handleTogglePermission(
                                              screen.id,
                                              action,
                                            )
                                          }
                                          className="border-gray-300 data-[state=checked]:bg-[#5B9BD5] data-[state=checked]:border-[#5B9BD5]"
                                        />
                                      </div>
                                    </td>
                                  ))}
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Mobile Card View */}
                    <div className="md:hidden space-y-3">
                      {modules.map((screen) => {
                        const modulePermissions =
                          formPermissions[screen.id] || {};
                        const allSelected = permissionActions.every(
                          (action) => modulePermissions[action],
                        );
                        const selectedCount = [
                          ...permissionActions,
                          "manageUsers" as const,
                        ].filter((action) => modulePermissions[action]).length;

                        return (
                          <div
                            key={screen.id}
                            className="border border-gray-200 rounded-xl p-3 bg-white shadow-sm"
                          >
                            {/* Module Header */}
                            <div className="flex items-start justify-between mb-3 pb-3 border-b border-gray-100">
                              <div className="flex-1 min-w-0 pr-2">
                                <div className="flex items-center gap-2">
                                  <p
                                    className="text-sm"
                                    style={{ color: "#5B9BD5" }}
                                  >
                                    {screen.screenName}
                                  </p>
                                  {selectedCount > 0 && (
                                    <span
                                      className="text-xs px-1.5 py-0.5 rounded"
                                      style={{
                                        backgroundColor: "#5B9BD5",
                                        color: "white",
                                      }}
                                    >
                                      {selectedCount}
                                    </span>
                                  )}
                                </div>
                                <p className="text-xs text-gray-500 mt-1 line-clamp-1">
                                  {" "}
                                  {screen.shortDescription}
                                </p>
                              </div>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleSelectAll(String(screen.id))}
                                className="h-7 px-2.5 text-xs whitespace-nowrap flex-shrink-0 rounded-lg transition-all"
                                style={
                                  allSelected
                                    ? {
                                        backgroundColor: "#5B9BD5",
                                        color: "white",
                                        borderColor: "#5B9BD5",
                                      }
                                    : {}
                                }
                              >
                                {allSelected ? "Clear" : "All"}
                              </Button>
                            </div>

                            {/* Permissions Grid */}
                            <div className="overflow-x-auto -mx-3 px-3 pb-2">
                              <div className="flex gap-2 min-w-max">
                                {permissionActions.map((action) => (
                                  <div
                                    key={action}
                                    className={`flex items-center space-x-2 p-2.5 rounded-lg border transition-all flex-shrink-0 ${modulePermissions[action] ? "border-[#5B9BD5]/30 bg-[#5B9BD5]/5" : "border-gray-200 bg-gray-50"}`}
                                  >
                                    <Checkbox
                                      id={`${screen.id}-${action}`}
                                      checked={modulePermissions[action] || false}
                                      onCheckedChange={() =>
                                        handleTogglePermission(screen.id, action)
                                      }
                                      className="border-gray-300 data-[state=checked]:bg-[#5B9BD5] data-[state=checked]:border-[#5B9BD5] w-4 h-4 md:w-auto md:h-auto"
                                      style={{
                                        minWidth: "auto",
                                        minHeight: "auto",
                                      }}
                                    />
                                    <label
                                      htmlFor={`${screen.id}-${action}`}
                                      className="text-xs text-gray-700 capitalize cursor-pointer select-none whitespace-nowrap"
                                    >
                                      {action}
                                    </label>
                                  </div>
                                ))}

                                <div
                                  className={`flex items-center space-x-2 p-2.5 rounded-lg border transition-all flex-shrink-0 ${modulePermissions.manageUsers ? "border-[#5B9BD5]/30 bg-[#5B9BD5]/5" : "border-gray-200 bg-gray-50"}`}
                                >
                                  <Checkbox
                                    id={`${screen.id}-manageUsers`}
                                    checked={
                                      modulePermissions.manageUsers || false
                                    }
                                    onCheckedChange={() =>
                                      handleTogglePermission(
                                        screen.id,
                                        manageUsersAction,
                                      )
                                    }
                                    className="border-gray-300 data-[state=checked]:bg-[#5B9BD5] data-[state=checked]:border-[#5B9BD5] w-4 h-4 md:w-auto md:h-auto"
                                    style={{
                                      minWidth: "auto",
                                      minHeight: "auto",
                                    }}
                                  />
                                  <label
                                    htmlFor={`${screen.id}-manageUsers`}
                                    className="text-xs text-gray-700 cursor-pointer select-none whitespace-nowrap"
                                  >
                                    Manage Users
                                  </label>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </TabsContent>
                ))}
              </Tabs>
            </CardContent>
          </Card>
        </div>
      </>
    );
  }

  // LIST VIEW (default)
  return (
    <>
      {loading && <FullScreenLoading />}
      {isSaving && <FullScreenLoading />}
      <div className="space-y-6 p-3 md:p-0">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="relative flex-1 max-w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />

            <Input
              placeholder="Search roles..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="h-10 pl-9 pr-9 shadow-sm"
            />

            {searchTerm && (
              <button
                type="button"
                onClick={() => {
                  setSearchTerm("");
                  setCurrentPage(1);
                }}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                title="Clear search"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          {canAdd && (
            <Button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleOpenForm();
              }}
              className="w-full sm:w-auto text-white shadow-lg hover:opacity-90 transition-opacity rounded-lg"
              style={{ backgroundColor: "#5B9BD5" }}
            >
              <Plus className="w-4 h-4 mr-2" />
              Create New Role
            </Button>
          )}
        </div>

        {/* Roles Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2" style={{ marginTop: "0px" }}>
          {roles.map((role) => {
              const enabledModules = role.enabledModules || [];
              const maxVisibleModules = 7;
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              const visibleModules = enabledModules.slice(0, maxVisibleModules);
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              const remainingCount = enabledModules.length - maxVisibleModules;

              return (
                <Card
                  key={role.id}
                  className="bg-white border border-gray-200 shadow-sm hover:shadow-md transition-shadow h-full flex flex-col rounded-xl"
                >
                  <CardContent className="p-4 flex-1 flex flex-col">
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#E8F3FC] flex items-center justify-center">
                        <Shield
                          className="w-5 h-5"
                          style={{ color: "#5B9BD5" }}
                        />
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <div className="space-y-1">
                            <h3 style={{ color: "#5B9BD5" }} className="text-base font-semibold leading-5">
                              {role.name}
                            </h3>
                            {role.isAvailableForDelegation && (
                              <Badge className="bg-green-100 text-green-700 border-green-300 text-xs">
                                Available for Delegation
                              </Badge>
                            )}
                          </div>
                          <div className="flex gap-1.5 flex-shrink-0">
                            {canEdit && ( 
                              <button
                                onClick={() => handleOpenForm(role)}
                                className="w-9 h-9 rounded-lg border border-gray-300 flex items-center justify-center hover:bg-gray-50 transition-colors"
                              >
                                <Edit className="w-3.5 h-3.5 text-gray-600" />
                              </button>
                            )}
                            {canDelete && !role.isSystemRole && (
                              <button
                                onClick={() => setRoleToDelete(role)}
                                className="w-8 h-8 rounded-lg border border-gray-300 flex items-center justify-center hover:bg-gray-50 transition-colors"
                              >
                                <Trash2 className="w-3.5 h-3.5 text-red-600" />
                              </button>
                            )}
                          </div>
                        </div>
                        <p className="text-sm text-gray-500 leading-5">
                          {role.description}
                        </p>
                      </div>
                    </div>

                    {/* Page Access Section */}
                    {/* <div className="flex-1">
                      <p className="text-gray-600 text-sm mb-3">Page Access:</p>
                      {enabledModules.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                          {visibleModules.map((moduleName, idx) => (
                            <Badge
                              key={idx}
                              className="text-xs px-3 py-1.5 rounded-md border-0 h-auto"
                              style={{
                                backgroundColor: "#E3F2FD",
                                color: "#5B9BD5",
                              }}
                            >
                              {moduleName}
                            </Badge>
                          ))}
                          {remainingCount > 0 && (
                            <Badge
                              className="text-xs px-3 py-1.5 rounded-md border-0 h-auto"
                              style={{
                                backgroundColor: "#E3F2FD",
                                color: "#5B9BD5",
                              }}
                            >
                              +{remainingCount} more
                            </Badge>
                          )}
                        </div>
                      ) : (
                        <p className="text-xs text-gray-400 italic">
                          No modules enabled
                        </p>
                      )}
                    </div> */}
                  </CardContent>
                </Card>
              );
            })
          }
        </div>

        <Pagination
          totalItems={totalCount}
          itemsPerPage={pageSize}
          currentPage={currentPage}
          onPageChange={(page) => setCurrentPage(page)}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setCurrentPage(1);
          }}
          label="roles"
        />

        {roleToDelete && (
          <div
            className="fixed inset-0 bg-black/40 flex items-center justify-center z-50"
            style={{ marginTop: "0px" }}
          >
            <div className="bg-white rounded-xl p-6 w-[90%] max-w-md shadow-xl">
              <h3 className="text-lg font-semibold mb-2 text-gray-800">
                Delete Role
              </h3>
              <p className="text-sm text-gray-600 mb-6">
                Are you sure you want to delete{" "}
                <span className="font-medium">{roleToDelete.name}</span>?
              </p>

              <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={() => setRoleToDelete(null)}>
                  Cancel
                </Button>

                <Button
                  className="bg-red-600 text-white hover:bg-red-700"
                  onClick={async () => {
                    await handleDeleteRole(roleToDelete);
                    setRoleToDelete(null);
                  }}
                >
                  Delete
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};
